import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Field } from '../ui/Field';
import { Spinner } from '../../ui/Spinner';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { createCustomer } from '../adminApi';
import { useRequestId } from '../../hooks/useRequestId';
import { ApiError } from '../../core/api/http';
import { errorMessage } from '../../core/errors';
import { customerCreateProblem, duplicateCandidates, duplicateReasonLabel, type CreateProblem } from '../../core/rules/customerMatch';
import { contactHint, customerName } from './customerRules';
import { ADMIN_BASE } from '../../utils/hosts';
import type { CustomerSummary } from '../../core/types/adminRequests';
type Props = {
  open: boolean;
  onClose: () => void;
  /** De geladen klantenlijst, voor de dubbelen-check terwijl je typt. */
  customers: readonly CustomerSummary[];
  /** Na aanmaken: de lijst verversen. */
  onCreated: () => void;
};
const empty = {
  firstName: '',
  lastName: '',
  phone: '',
  email: ''
};

/**
 * "Nieuwe klant": aan de balie of overgezet uit Salonized. Telefoon óf e-mail is genoeg. Lijkt ze
 * op een bestaande klant (zelfde e-mail, telefoon of volledige naam), dan zie je dat vóór het
 * aanmaken, met een link naar dat dossier; "Toch aanmaken" blijft mogelijk (moeder en dochter).
 */
export function NewCustomerDialog({
  open,
  onClose,
  customers,
  onCreated
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [problem, setProblem] = useState<CreateProblem | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const requestId = useRequestId(JSON.stringify(form));
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setForm(empty);
      setProblem(null);
      setAcknowledged(false);
      d.showModal();
      firstRef.current?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);
  const duplicates = useMemo(() => duplicateCandidates(customers, form).slice(0, 3), [customers, form]);
  // Elke wijziging die de dubbelen verandert, vraagt opnieuw om bevestiging.
  const duplicateKey = duplicates.map(d => d.customer.id).join(',');
  useEffect(() => setAcknowledged(false), [duplicateKey]);
  const set = (key: keyof typeof empty) => (value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    if (problem) setProblem(null);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (lock.current) return;
    const found = customerCreateProblem(form);
    if (found) {
      setProblem(found);
      return;
    }
    if (duplicates.length > 0 && !acknowledged) {
      setAcknowledged(true);
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      const {
        id
      } = await createCustomer({
        requestId,
        ...form
      });
      toast.success(`${form.firstName.trim()} staat bij je klanten.`);
      onCreated();
      onClose();
      navigate(`${ADMIN_BASE}/klanten/${id}`);
    } catch (err) {
      const existing = err instanceof ApiError && err.code === 'duplicate' ? err.fields?.duplicateId : undefined;
      if (existing) {
        setProblem({
          field: 'email',
          message: err instanceof ApiError ? err.message : 'Dit e-mailadres bestaat al.'
        });
      } else {
        toast.error(errorMessage(err) ?? 'Aanmaken lukte niet. Probeer het opnieuw.');
      }
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const fieldError = (f: CreateProblem['field']) => problem && (problem.field === f || problem.field === 'contact' && (f === 'phone' || f === 'email')) ? problem.message : undefined;
  return <dialog ref={ref} aria-labelledby="new-customer-title" onCancel={e => {
    e.preventDefault();
    if (!busy) onClose();
  }} className="w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-line bg-shell p-0 text-ink shadow-xl backdrop:bg-ink/40" data-id="element-1459" data-mp="3rlt815ys0o3x">
      <form onSubmit={submit} noValidate className="p-5" data-id="element-1460" data-mp="214pam9h6lf6x">
        <h2 id="new-customer-title" className="text-[16px] font-medium text-ink" data-id="element-1461" data-mp="25353uv3uvjss">
          Nieuwe klant
        </h2>
        <p className="mt-1 text-[14px] text-muted" data-id="element-1462" data-mp="2a4uhmiigqbkk">Telefoon of e-mail is genoeg. Met een e-mailadres kan ze later zelf inloggen via een inloglink.</p>

        <div className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-1463" data-mp="2k179rbyd0hd7">
          <Field label="Voornaam" data-id="element-1464" data-mp="2jzmfzojvd2ge">
            <input ref={firstRef} value={form.firstName} onChange={e => set('firstName')(e.target.value)} autoComplete="off" maxLength={80} aria-invalid={problem?.field === 'firstName' || undefined} className={inputCls} data-id="element-1465" data-mp="2os5vws6149od" />
          </Field>
          <Field label="Achternaam" hint="Optioneel" data-id="element-1466" data-mp="2xu6o3ameo5j6">
            <input value={form.lastName} onChange={e => set('lastName')(e.target.value)} autoComplete="off" maxLength={80} className={inputCls} data-id="element-1467" data-mp="32ryh67ysdxoy" />
          </Field>
          <Field label="Telefoon" data-id="element-1468" data-mp="3cx6qa5ie50fv">
            <input type="tel" value={form.phone} onChange={e => set('phone')(e.target.value)} autoComplete="off" maxLength={30} aria-invalid={Boolean(fieldError('phone')) || undefined} className={inputCls} data-id="element-1469" data-mp="37x288b1u3xnj" />
          </Field>
          <Field label="E-mailadres" data-id="element-1470" data-mp="2ywvvirdyn1pf">
            <input type="email" value={form.email} onChange={e => set('email')(e.target.value)} autoComplete="off" maxLength={190} aria-invalid={Boolean(fieldError('email')) || undefined} className={inputCls} data-id="element-1471" data-mp="2yxxdlfte8ogn" />
          </Field>
        </div>

        {problem && <p role="alert" className="mt-3 text-[13px] text-clay" data-id="element-1472" data-mp="3lvifq47its6q">
            {problem.message}
          </p>}

        {duplicates.length > 0 && <div className="mt-4 rounded-md bg-sand px-3 py-3" role={acknowledged ? 'alert' : 'status'} data-id="element-1473" data-mp="i7gqk2466qgv">
            <p className="text-[14px] text-ink" data-id="element-1474" data-mp="dh0ccfsiz0tg">{duplicates.length === 1 ? 'Lijkt op een bestaande klant:' : 'Lijkt op bestaande klanten:'}</p>
            <ul className="mt-2 space-y-1.5" data-id="element-1475" data-mp="27px2je7o8928">
              {duplicates.map(d => <li key={d.customer.id} className="text-[13px]" data-id="element-1476" data-mp="2cmnky86y7gqt">
                  <Link to={`${ADMIN_BASE}/klanten/${d.customer.id}`} onClick={onClose} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-1477" data-mp="1xz2h2c291dhu">
                    {customerName(d.customer)}
                  </Link>
                  <span className="text-muted" data-id="element-1478" data-mp="1oxusilpjbr0y">
                    {' '}
                    · {d.reasons.map(r => duplicateReasonLabel[r]).join(', ')}
                    {contactHint(d.customer) && ` · ${contactHint(d.customer)}`}
                  </span>
                </li>)}
            </ul>
            {acknowledged && <p className="mt-2 text-[13px] text-muted" data-id="element-1479" data-mp="1jz9umsxr9jfo">Is het toch iemand anders? Klik nog een keer op Toch aanmaken.</p>}
          </div>}

        <div className="mt-5 flex flex-wrap justify-end gap-2" data-id="element-1480" data-mp="3ecsq7x886ryc">
          <button type="button" onClick={onClose} disabled={busy} className={btnGhost} data-id="element-1481" data-mp="3eaybj0njrw2p">
            Annuleren
          </button>
          <button type="submit" disabled={busy} aria-busy={busy || undefined} className={btnPrimary} data-id="element-1482" data-mp="xqw3fjmd9ij1">
            {busy && <Spinner data-id="element-1483" data-mp="1cg0mt44wdssq" />}
            {busy ? 'Aanmaken…' : duplicates.length > 0 && acknowledged ? 'Toch aanmaken' : 'Klant aanmaken'}
          </button>
        </div>
      </form>
    </dialog>;
}